import React, { useState, useEffect } from 'react';
import { settingsAPI } from '../utils/api';
import { useSettings } from '../hooks/useSettings';
import toast from 'react-hot-toast';

const API_BASE = process.env.REACT_APP_API_URL?.replace('/api', '') || '';

export default function Settings() {
  const { settings, setSettings } = useSettings();
  const [form, setForm] = useState({ ...settings });
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [saving, setSaving] = useState(false);
const [headerFile, setHeaderFile] = useState(null);
const [headerPreview, setHeaderPreview] = useState(null);
const [footerFile, setFooterFile] = useState(null);
const [footerPreview, setFooterPreview] = useState(null);

  useEffect(() => { setForm({ ...settings }); }, [settings]);

  const setField = (k, v) => setForm(f => ({ ...f, [k]: v }));

const handleImageChange = (e, type) => {
  const file = e.target.files[0];
  if (!file) return;
  const reader = new FileReader();
  reader.onload = ev => {
    if (type === 'logo') { setLogoFile(file); setLogoPreview(ev.target.result); }
    if (type === 'header') { setHeaderFile(file); setHeaderPreview(ev.target.result); }
    if (type === 'footer') { setFooterFile(file); setFooterPreview(ev.target.result); }
  };
  reader.readAsDataURL(file);
};

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const fd = new FormData();
      const fields = ['company_name', 'company_tagline', 'address', 'phone1', 'phone2',
        'email1', 'email2', 'website', 'footer_note', 'bill_prefix',
        'receipt_prefix', 'currency', 'primary_color', 'accent_color'];
      fields.forEach(k => fd.append(k, form[k] || ''));
if (logoFile) fd.append('logo', logoFile);
if (headerFile) fd.append('header_image', headerFile);
if (footerFile) fd.append('footer_image', footerFile);
      const res = await settingsAPI.update(fd);
      setSettings(res.data);
      toast.success('Settings saved!');
    } catch {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const currentLogo = logoPreview || (settings.company_logo ? `${API_BASE}${settings.company_logo}` : null);

  return (
    <div style={{ maxWidth: 800 }}>
      <h1 className="page-title">Settings</h1>
      <p style={{ color: '#64748b', marginBottom: 24, marginTop: -12 }}>
        Configure your company info, branding, and document preferences. These appear in bill and receipt headers/footers.
      </p>

      <form onSubmit={handleSubmit}>
        {/* Company Info */}
        <div className="card" style={{ marginBottom: 20 }}>
          <div className="card-header"><span className="card-title">🏢 Company Information</span></div>
          <div className="card-body">
            {/* Logo */}
            <div className="form-group">
              <label className="form-label">Company Logo</label>
              <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
                {currentLogo ? (
                  <img src={currentLogo} alt="Logo" style={{ maxHeight: 60, maxWidth: 160, borderRadius: 8, border: '1px solid #e2e8f0', padding: 4, background: '#f8fafc' }} />
                ) : (
                  <div style={{ width: 100, height: 60, borderRadius: 8, border: '2px dashed #e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#94a3b8', fontSize: 12 }}>
                    No Logo
                  </div>
                )}
                <div>
                  <input type="file" accept="image/*" id="logo-input" style={{ display: 'none' }} onChange={e => handleImageChange(e, 'logo')} />
                  <label htmlFor="logo-input" className="btn btn-outline btn-sm" style={{ cursor: 'pointer' }}>
                    📷 Upload Logo
                  </label>
                  <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 4 }}>PNG, JPG up to 5MB</div>
                </div>
              </div>
            </div>
{/* Header Image */}
<div className="form-group">
  <label className="form-label">Bill / Receipt Header Image</label>
  <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
    {(headerPreview || settings.header_image) && (
      <img
        src={headerPreview || `${API_BASE}${settings.header_image}`}
        alt="Header"
        style={{ maxHeight: 80, width: '100%', maxWidth: 400, objectFit: 'contain', borderRadius: 8, border: '1px solid #e2e8f0' }}
      />
    )}
    <div>
      <input type="file" accept="image/png,image/jpeg" id="header-input" style={{ display: 'none' }} onChange={e => handleImageChange(e, 'header')} />
      <label htmlFor="header-input" className="btn btn-outline btn-sm" style={{ cursor: 'pointer' }}>
        🖼 Upload Header PNG
      </label>
      <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 4 }}>Replaces the text header. PNG recommended.</div>
    </div>
  </div>
</div>

{/* Footer Image */}
<div className="form-group">
  <label className="form-label">Bill / Receipt Footer Image</label>
  <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
    {(footerPreview || settings.footer_image) && (
      <img
        src={footerPreview || `${API_BASE}${settings.footer_image}`}
        alt="Footer"
        style={{ maxHeight: 80, width: '100%', maxWidth: 400, objectFit: 'contain', borderRadius: 8, border: '1px solid #e2e8f0' }}
      />
    )}
    <div>
      <input type="file" accept="image/png,image/jpeg" id="footer-input" style={{ display: 'none' }} onChange={e => handleImageChange(e, 'footer')} />
      <label htmlFor="footer-input" className="btn btn-outline btn-sm" style={{ cursor: 'pointer' }}>
        🖼 Upload Footer PNG
      </label>
      <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 4 }}>Replaces the text footer. PNG recommended.</div>
    </div>
  </div>
</div>
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Company Name *</label>
                <input className="form-control" value={form.company_name || ''} onChange={e => setField('company_name', e.target.value)} required />
              </div>
              <div className="form-group">
                <label className="form-label">Tagline / Subtitle</label>
                <input className="form-control" value={form.company_tagline || ''} onChange={e => setField('company_tagline', e.target.value)} placeholder="e.g. Software Solutions" />
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">Address</label>
              <textarea className="form-control" rows={2} value={form.address || ''} onChange={e => setField('address', e.target.value)} placeholder="Full business address" />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Phone 1</label>
                <input className="form-control" value={form.phone1 || ''} onChange={e => setField('phone1', e.target.value)} placeholder="+880..." />
              </div>
              <div className="form-group">
                <label className="form-label">Phone 2</label>
                <input className="form-control" value={form.phone2 || ''} onChange={e => setField('phone2', e.target.value)} placeholder="+880..." />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Email 1</label>
                <input type="email" className="form-control" value={form.email1 || ''} onChange={e => setField('email1', e.target.value)} />
              </div>
              <div className="form-group">
                <label className="form-label">Email 2</label>
                <input type="email" className="form-control" value={form.email2 || ''} onChange={e => setField('email2', e.target.value)} />
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">Website</label>
              <input className="form-control" value={form.website || ''} onChange={e => setField('website', e.target.value)} placeholder="www.yourcompany.com" />
            </div>

            <div className="form-group">
              <label className="form-label">Footer Note / Thank You Message</label>
              <input className="form-control" value={form.footer_note || ''} onChange={e => setField('footer_note', e.target.value)} placeholder="Thank you for your business!" />
            </div>
          </div>
        </div>

        {/* Document Settings */}
        <div className="card" style={{ marginBottom: 20 }}>
          <div className="card-header"><span className="card-title">📄 Document Settings</span></div>
          <div className="card-body">
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Bill Number Prefix</label>
                <input className="form-control" value={form.bill_prefix || ''} onChange={e => setField('bill_prefix', e.target.value)} placeholder="BILL" />
                <span style={{ fontSize: 12, color: '#94a3b8', marginTop: 4, display: 'block' }}>e.g. prefix "BILL" → BILL10001</span>
              </div>
              <div className="form-group">
                <label className="form-label">Receipt Number Prefix</label>
                <input className="form-control" value={form.receipt_prefix || ''} onChange={e => setField('receipt_prefix', e.target.value)} placeholder="RCP" />
                <span style={{ fontSize: 12, color: '#94a3b8', marginTop: 4, display: 'block' }}>e.g. prefix "RCP" → RCP10001</span>
              </div>
              <div className="form-group">
                <label className="form-label">Currency Code</label>
                <input className="form-control" value={form.currency || ''} onChange={e => setField('currency', e.target.value)} placeholder="BDT" />
                <span style={{ fontSize: 12, color: '#94a3b8', marginTop: 4, display: 'block' }}>e.g. BDT, USD, EUR</span>
              </div>
            </div>
          </div>
        </div>

        {/* Branding Colors */}
        <div className="card" style={{ marginBottom: 24 }}>
          <div className="card-header"><span className="card-title">🎨 Branding Colors</span></div>
          <div className="card-body">
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Primary Color (Header / Table)</label>
                <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                  <input type="color" value={form.primary_color || '#2563eb'} onChange={e => setField('primary_color', e.target.value)} style={{ width: 50, height: 40, border: '1.5px solid #e2e8f0', borderRadius: 8, cursor: 'pointer', padding: 2 }} />
                  <input className="form-control" value={form.primary_color || ''} onChange={e => setField('primary_color', e.target.value)} placeholder="#2563eb" style={{ flex: 1 }} />
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Accent Color</label>
                <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                  <input type="color" value={form.accent_color || '#93c5fd'} onChange={e => setField('accent_color', e.target.value)} style={{ width: 50, height: 40, border: '1.5px solid #e2e8f0', borderRadius: 8, cursor: 'pointer', padding: 2 }} />
                  <input className="form-control" value={form.accent_color || ''} onChange={e => setField('accent_color', e.target.value)} placeholder="#93c5fd" style={{ flex: 1 }} />
                </div>
              </div>
            </div>

            {/* Color Preview */}
            <div style={{ marginTop: 12, padding: '12px 16px', borderRadius: 10, border: '1.5px solid #e2e8f0', background: '#f8fafc' }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: '#64748b', marginBottom: 8 }}>PREVIEW</div>
              <div style={{ background: form.primary_color || '#2563eb', color: '#fff', borderRadius: 8, padding: '10px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontWeight: 600 }}>{form.company_name || 'Company Name'}</span>
                <span style={{ fontSize: 22, fontWeight: 700, opacity: .5, letterSpacing: 2 }}>BILL</span>
              </div>
            </div>
          </div>
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <button type="submit" className="btn btn-primary" disabled={saving}>
            {saving ? 'Saving...' : '💾 Save Settings'}
          </button>
        </div>
      </form>
    </div>
  );
}
